/**
 * Zentraler Client für alle Claude-Generierungs-Endpunkte.
 *
 * Stellt zwei Dinge sicher:
 *   1. Alle Aufrufe gehen durch dieselbe Guard — 409/429 landen immer in
 *      einheitlicher Fehlerbehandlung, egal ob Admin-Popup oder Direct-Flow.
 *   2. Die strukturierten Backend-Fehler ({ code, message, retryAfterSeconds })
 *      werden in vordefinierte deutsche Meldungen übersetzt, damit kein
 *      Endpunkt vergisst, sie zu zeigen.
 *
 * Fehlercodes des Backends (api/src/common/error-codes.ts):
 *   - ALREADY_RUNNING (409): Für diese Card+Action läuft bereits eine Generierung.
 *   - RATE_LIMITED (429): Throttler hat zugeschlagen, retryAfterSeconds ist gesetzt.
 */

import { API_BASE } from './apiBase';

export type GenerationErrorCode =
  | 'ALREADY_RUNNING'
  | 'RATE_LIMITED'
  | 'SECURITY_BLOCKED'
  | 'BLOCKED'
  | 'INVALID_CARD'
  | 'GEN_CARD_LIMIT_REACHED'
  | 'NETWORK'
  | 'UNKNOWN';

export interface GenerationError {
  code: GenerationErrorCode;
  message: string;
  retryAfterSeconds?: number;
  /** Raw warnings vom Sicherheits-Layer, falls SECURITY_BLOCKED. */
  warnings?: string[];
}

export type GenerationResult<T> =
  | { ok: true; data: T; warnings?: string[] }
  | { ok: false; error: GenerationError };

/**
 * Deutsche Meldungen pro Code — zentral gepflegt, damit kein Caller sie neu erfindet.
 * Placeholder `{seconds}` wird von `formatGenerationError()` ersetzt.
 */
export const GERMAN_ERROR_MESSAGES: Record<GenerationErrorCode, string> = {
  ALREADY_RUNNING:
    'Für diesen Abschnitt läuft bereits eine Generierung. Bitte warten Sie, bis das Ergebnis erscheint.',
  RATE_LIMITED:
    'Zu viele Anfragen in kurzer Zeit. Nächster Versuch in etwa {seconds} Sekunden möglich.',
  SECURITY_BLOCKED:
    'Die Eingabe wurde aus Sicherheitsgründen abgelehnt. Bitte formulieren Sie die fachliche Aufgabe direkter.',
  BLOCKED:
    'Ihr Konto ist aus Sicherheitsgründen vorübergehend für KI-Generierungen gesperrt (noch {seconds} Sekunden). Bei Fragen wenden Sie sich an den Support.',
  INVALID_CARD:
    'Unbekannter Abschnitt. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
  GEN_CARD_LIMIT_REACHED:
    'Limit für diesen Abschnitt erreicht. Wenden Sie sich an den Administrator.',
  NETWORK:
    'Server nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.',
  UNKNOWN: 'Die Generierung ist fehlgeschlagen. Bitte erneut versuchen.',
};

export function formatGenerationError(err: GenerationError): string {
  const tpl = GERMAN_ERROR_MESSAGES[err.code] || err.message;
  if (
    (err.code === 'RATE_LIMITED' || err.code === 'BLOCKED') &&
    err.retryAfterSeconds !== undefined
  ) {
    return tpl.replace('{seconds}', String(err.retryAfterSeconds));
  }
  return tpl;
}

async function postJson<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<GenerationResult<T>> {
  const token = localStorage.getItem('auth_token');

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      return {
        ok: false,
        error: { code: 'NETWORK', message: 'Zeitüberschreitung – bitte erneut versuchen.' },
      };
    }
    return {
      ok: false,
      error: { code: 'NETWORK', message: GERMAN_ERROR_MESSAGES.NETWORK },
    };
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    // Strukturierte Fehler vom Backend: { code, message, retryAfterSeconds? }
    const code = (data?.code as GenerationErrorCode) || 'UNKNOWN';
    const retryAfterSeconds =
      typeof data?.retryAfterSeconds === 'number'
        ? data.retryAfterSeconds
        : undefined;

    // Fallback für nicht-strukturierte Fehler
    const KNOWN_CODES: GenerationErrorCode[] = [
      'ALREADY_RUNNING',
      'RATE_LIMITED',
      'BLOCKED',
      'INVALID_CARD',
      'GEN_CARD_LIMIT_REACHED',
    ];
    if (!KNOWN_CODES.includes(code)) {
      return {
        ok: false,
        error: {
          code: 'UNKNOWN',
          message: data?.message || `Fehler ${response.status}`,
        },
      };
    }

    return {
      ok: false,
      error: {
        code,
        message: data?.message || GERMAN_ERROR_MESSAGES[code],
        retryAfterSeconds,
      },
    };
  }

  // Erfolg: aber prüfen auf Sicherheits-Block (kein HTTP-Fehler, nur warnings)
  const warnings: string[] = Array.isArray(data?.warnings) ? data.warnings : [];
  const securityBlocked = warnings.some((w) =>
    (w || '').includes('Sicherheitsgründen'),
  );
  if (securityBlocked) {
    return {
      ok: false,
      error: {
        code: 'SECURITY_BLOCKED',
        message: GERMAN_ERROR_MESSAGES.SECURITY_BLOCKED,
        warnings,
      },
    };
  }

  return { ok: true, data: data as T, warnings };
}

/**
 * Bricht eine laufende/hängende Generierung serverseitig ab (gibt den Lock frei),
 * damit der Nutzer sofort neu generieren kann. Ohne Argument werden ALLE Locks
 * des Users freigegeben (für den „Zurücksetzen"-Button bei „bereits in Generierung").
 * Best-effort: Fehler werden geschluckt. `keepalive` sorgt dafür, dass der Call
 * auch bei Seiten-Reload/Tab-Schließen noch zuverlässig rausgeht.
 */
export async function cancelGeneration(payload?: { cardId?: string; action?: string }): Promise<void> {
  const token = localStorage.getItem('auth_token');
  try {
    await fetch(`${API_BASE}/api/prompts/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
      keepalive: true,
    });
  } catch { /* best effort — Backend gibt den Lock spätestens bei Disconnect/TTL frei */ }
}

export interface GenerateV2Payload {
  cardId: string;
  /**
   * Optionaler Section-Prompt-Override — wird vom Backend NUR für Admins
   * akzeptiert (Live-Test im Admin-Popup). Non-Admin-Clients lassen das
   * Feld weg; der Prompt wird serverseitig aus der DB / Default geladen.
   */
  sectionPrompt?: string;
  formData: Record<string, any>;
  customerData?: {
    name?: string;
    branche?: string;
    rechtsform?: string;
    ort?: string;
  };
  customerId?: number;
  model?: 'sonnet' | 'opus';
}

export interface GenerateV2Response {
  text: string;
  warnings?: string[];
}

export interface SuggestBulkPayload {
  sectionId: string;
  context: Record<string, any>;
  customPrompt?: string;
}

/**
 * Startet eine Text-Generierung via POST /api/prompts/generate-v2.
 */
export async function generateV2(
  payload: GenerateV2Payload,
  signal?: AbortSignal,
): Promise<GenerationResult<GenerateV2Response>> {
  return postJson<GenerateV2Response>('/api/prompts/generate-v2', payload, signal);
}

/**
 * Verfeinert einen einzelnen Listenpunkt via POST /api/prompts/refine-item.
 */
export async function refineItem(
  payload: { itemHtml: string; instruction?: string; mode?: 'edit' | 'regenerate' },
  signal?: AbortSignal,
): Promise<GenerationResult<{ text: string }>> {
  return postJson<{ text: string }>('/api/prompts/refine-item', payload, signal);
}

/**
 * Startet Bulk-Vorschläge via POST /api/prompts/suggest-bulk.
 */
export async function suggestBulk<T = unknown>(
  payload: SuggestBulkPayload | Record<string, any>,
  signal?: AbortSignal,
): Promise<GenerationResult<T>> {
  return postJson<T>('/api/prompts/suggest-bulk', payload, signal);
}
